'use client'

import React, {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import ModalContext from '../ModalProvider/ModalContext'
import TXT from '@/messages'

/**
 * @typedef ButtonState
 * @property {string} text - The text displayed on the button.
 * @property {Function} action - The function to be executed when the button is clicked.
 * @property {boolean} disabled - Whether the button is disabled or not.
 */
export type ButtonState = {
  text: string | React.ReactNode;
  action: () => Promise<void>
  disabled: boolean;
  actionId?: number;
  /**
   * Is the payment a digital wallet payment? This is used in button display
   */
  digitalWalletPayment?: boolean;
}

/**
 * @typedef ButtonContextProps
 * @property {ButtonState} buttonState - The current state of the button.
 * @property {React.Dispatch<Partial<ButtonState>>} updateButtonState - Function to update the button state.
 * @property {Function} resetButtonState - Function to reset the button state to default.
 */
export type ButtonContextProps = {
  buttonState: ButtonState;
  updateButtonState: React.Dispatch<Partial<ButtonState>>;
  resetButtonState: () => void;
  loading: boolean
}

const defaultContext: ButtonContextProps = {
  buttonState: {
    text: TXT.buttons.addToOrder,
    action: () => Promise.resolve(),
    disabled: true,
    actionId: undefined,
    digitalWalletPayment: false,
  },
  updateButtonState: () => undefined,
  resetButtonState: () => undefined,
  loading: false,
}

/**
 * Button context to provide state and state management functions for buttons.
 * @type {React.Context<ButtonContextProps>}
 */
export const ButtonContext = React.createContext<ButtonContextProps>(defaultContext)

// styles defined in transitions.css
const LOADING_TEXT = (
  <span className="btn-loading">
    <span>.</span>
    <span>.</span>
    <span>.</span>
  </span>
)

/**
 * ButtonProvider is a React functional component that provides a context for managing button states.
 * @returns The ButtonContext.Provider component with the provided value.
 */
const ButtonProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  // Loading belongs to the currently rendered button action, not to the provider globally.
  const actionIdRef = useRef(0)
  const [loadingActionId, setLoadingActionId] = useState<number | null>(null)
  // Context
  const { modalState } = useContext(ModalContext)

  // set loading to false if the error modal is open
  useEffect(() => {
    if (modalState.open) {
      setLoadingActionId(null)
    }
  }, [modalState.open])

  const [buttonState, dispatch] = useReducer(
    (oldState: ButtonState, newState: Partial<ButtonState>) => ({ ...oldState, ...newState }),
    {
      text: defaultContext.buttonState.text,
      action: defaultContext.buttonState.action,
      disabled: defaultContext.buttonState.disabled,
      actionId: defaultContext.buttonState.actionId,
      digitalWalletPayment: defaultContext.buttonState.digitalWalletPayment,
    },
  )

  const updateButtonState = useCallback((newState: Partial<ButtonState>) => {
    let nextState = newState
    if (newState.action && newState !== defaultContext.buttonState) {
      // Stamp each wrapped action so stale in-flight actions cannot keep a newer step button loading.
      const actionId = actionIdRef.current + 1
      actionIdRef.current = actionId
      nextState = {
        ...newState,
        actionId,
        action: async () => {
          setLoadingActionId(actionId)
          try {
            await newState.action?.()
          } finally {
            setLoadingActionId((currentLoadingActionId) => (
              currentLoadingActionId === actionId ? null : currentLoadingActionId
            ))
          }
        },
      }
    }

    dispatch(nextState)
  }, [dispatch])

  const resetButtonState = useCallback(() => {
    dispatch(defaultContext.buttonState)
  }, [dispatch])

  const loading = buttonState.actionId !== undefined
    && loadingActionId === buttonState.actionId

  const value = useMemo(() => ({
    buttonState: loading ? { ...buttonState, text: LOADING_TEXT, disabled: true } : buttonState,
    updateButtonState,
    resetButtonState,
    loading,
  }), [
    buttonState,
    updateButtonState,
    resetButtonState,
    loading,
  ])

  return (
    <ButtonContext value={value}>
      {children}
    </ButtonContext>
  )
}

export default ButtonProvider
